import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";

import FlashContext from "components/FlashMessages/FlashContext";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import { getDrawerFormFix } from "utils/css";
import { getDocsUrl } from "utils/getDocsUrl";
import { withTestId } from "utils/withTestId";
import Textarea from "ds/components/Textarea";
import Box from "ds/components/Box";
import FormFieldRoleAssignment from "components/FormFields/FormFieldRoleAssignment";

import { ADD_GROUP_DRAWER_TEST_ID } from "./constants";
import { AddGroupFields } from "./types";
import useAddGroup from "./useAddGroup";
import { USER_ROLES_FOR_GROUP_MAPPING } from "./gql";

const IdpGroupMappingMapGroupDrawer = createDrawer(() => {
  const [assignRolesFormHasDirtyFields, setAssignRolesFormHasDirtyFields] = useState(false);

  const { data, loading } = useQuery(USER_ROLES_FOR_GROUP_MAPPING);

  const availableRoles = useMemo(() => data?.roles || [], [data?.roles]);

  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const groupMapForm = useForm<AddGroupFields>({
    defaultValues: {
      name: "",
      description: "",
      assignedRoles: [],
    },
    mode: "onChange",
  });
  const drawer = useModal();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = groupMapForm;

  const { onCreate: onCreateGroup } = useAddGroup();

  const onSubmit: SubmitHandler<AddGroupFields> = (formData) => {
    const input = {
      groupName: formData.name,
      description: formData.description?.trim() || null,
      roleBindings: formData.assignedRoles.map((role) => ({
        roleID: role.roleID,
        spaceID: role.spaceID,
      })),
    };

    onCreateGroup(input)
      .then(({ data }) => {
        if (data) {
          reportSuccess({
            message: `Group access was added`,
          });

          drawer.hide();
        }
      })
      .catch(onError);
  };

  return (
    <DrawerForm
      isDirty={isDirty || assignRolesFormHasDirtyFields}
      {...withTestId(ADD_GROUP_DRAWER_TEST_ID)}
    >
      <FormProvider {...groupMapForm}>
        <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Map IdP group" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody>
            <FormField
              label="Group ID"
              tooltipMode="parent"
              tooltipInfo={
                <>
                  <TooltipModalTitle>Group ID</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    Type in any group identifier that you have defined in your IdP (e.g., Name in
                    GitHub, Path in GitLab, ID in Microsoft Entra). Naming may vary depending on
                    your provider.
                    <ReadMoreDocsLink
                      docsUrl={getDocsUrl(
                        "/concepts/identity-access-management#access-through-idp-group-mapping"
                      )}
                    />
                  </TooltipModalBody>
                </>
              }
              tooltipInfoVariant="modal"
              error={errors?.name?.message}
              helperText="Enter the matching group identifier from your IdP provider"
            >
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter group ID"
                  error={!!errors?.name}
                  {...register("name", {
                    required: "Group ID is required.",
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <FormField label="Description" isOptional>
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter group description here..."
                  maxRows={15}
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>
            <Box direction="column" gap="large" fullWidth padding="x-large 0 0 0">
              <FormFieldRoleAssignment
                name="assignedRoles"
                availableRoles={availableRoles}
                loading={loading}
                setHasDirtyFields={setAssignRolesFormHasDirtyFields}
                analyticsPage={AnalyticsPageOrganization.OrganizationIdpGroupMapping}
              />
            </Box>

            <DrawerFooter>
              <DrawerFooterActions>
                <DrawerCancelButton />
                <Button variant="primary" type="submit">
                  Add
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showIdpGroupMappingMapGroupDrawer = createDrawerTrigger(IdpGroupMappingMapGroupDrawer);
